/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ApiStatus } from '../../../../ts';
import { Instance } from '../../../../ts/instance';
import { RootState } from '../../../../ts/state';
import localStorage, { LocalStorage } from '../../util/localStorage';
import { adapter } from './adapter';

/**
 * The selectors provided out-of-the-box by the entity adapter for the "instances" slice.
 */
export const selectors = adapter.getSelectors(
  (state: RootState) => state.instances
);

export const selectInstanceById = selectors.selectById;

/**
 * Selector to test whether instances are loading
 */
export const areInstancesLoading = (state: RootState): boolean => {
  return state.instances.listNewStatus === ApiStatus.LOADING;
};

/**
 * Selector to test whether instances are updating
 */
export const areInstancesUpdating = (state: RootState): boolean => {
  return state.instances.listUpdatedStatus === ApiStatus.LOADING;
};

/**
 * Selector to get all instances
 */
export const selectAllInstances = selectors.selectAll;

/**
 * Selector to get the array of the names of the accounts the user could not access
 * (and from which we could not retrieve instances)
 */
export const selectInaccessibleAccountNames = (state: RootState): string[] =>
  state.instances.inaccessibleAccountNames;

/**
 * True if any instances have been retrieved, otherwise false.
 */
export const hasInstances = (state: RootState): boolean =>
  selectors.selectTotal(state) > 0;

/**
 * Selector for instance information that may be cached in local storage.
 */
export const selectCachedInstances = (): Instance[] =>
  localStorage.get(LocalStorage.INSTANCES);
